import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  billsCount,
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  COMMITTEE_ROLES,
  cleanPhone,
  DOCUMENT_TITLES,
  describeError,
  fullDate,
  isReachable,
  isUnnamed,
  MEMBER_ERRORS,
  MEMBER_STATUS_LABELS,
  METER_STATUS_LABELS,
  type MemberFormValues,
  type NotifyChannel,
  PHONE_HINT,
  PHONE_STATUS_LABELS,
  peopleCount,
  present,
  SHART_ERRORS,
  todayInMorocco,
  toMemberFields,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Skeleton,
  Tag,
} from 'antd';
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from 'urql';
import { DeskPanel, useDirty } from './panel';
import {
  ChangeMemberStatusDocument,
  CreateMemberDocument,
  CreateShartPersonDocument,
  DeleteShartPersonDocument,
  type MemberDetails,
  MemberDocument,
  peopleContext,
  RenameShartPersonDocument,
  UpdateMemberDocument,
} from './people-data';
import { useIsCommittee } from './water-data';

const FIELD_LABELS: Record<keyof ReturnType<typeof toMemberFields>, string> = {
  firstName: 'الاسم الشخصي',
  fatherName: 'اسم الأب',
  lastName: 'الاسم العائلي',
  sex: 'الجنس',
  memberAddress: 'العنوان',
  phone: 'هاتف العضو',
  contactPhone: 'هاتف القريب',
  contactName: 'صاحب رقم القريب',
  notifyChannel: 'طريقة التواصل',
  email: 'البريد الإلكتروني',
  committeeRole: 'العضوية في اللجنة',
};

function show(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'sex') return value === 'FEMALE' ? 'أنثى' : 'ذكر';
  if (key === 'notifyChannel') return CHANNEL_LABELS[value as NotifyChannel];
  if (key === 'committeeRole') {
    return COMMITTEE_ROLE_LABELS[value as keyof typeof COMMITTEE_ROLE_LABELS];
  }
  return String(value);
}

const phoneRule = {
  validator: (_: unknown, value: string | undefined) => {
    const result = cleanPhone(value ?? '');
    return 'error' in result
      ? Promise.reject(new Error(result.error))
      : Promise.resolve();
  },
};

const EMPTY: MemberFormValues = {
  firstName: '',
  fatherName: '',
  lastName: '',
  sex: 'MALE',
  memberAddress: '',
  phone: '',
  contactPhone: '',
  contactName: '',
  notifyChannel: 'WHATSAPP',
  email: '',
  committeeRole: null,
};

const valuesOf = (m: MemberDetails): MemberFormValues => ({
  firstName: m.firstName,
  fatherName: m.fatherName,
  lastName: m.lastName,
  sex: m.sex,
  memberAddress: m.memberAddress,
  phone: m.phone ?? '',
  contactPhone: m.contactPhone ?? '',
  contactName: m.contactName ?? '',
  notifyChannel: m.notifyChannel,
  email: m.email ?? '',
  committeeRole: m.committeeRole,
});

/** A member: details, shart people, documents, meters, leaving. */
export function MemberPanel({
  id,
  onClose,
  onOpen,
  onDirtyChange,
  inline = false,
  formOnly = false,
  onDone,
}: {
  /** Inside the member's page (doc 58): its header shows the summary. */
  inline?: boolean;
  /** Only the form, inside a page's section (doc 60). */
  formOnly?: boolean;
  /** After saving or «إلغاء» (formOnly). */
  onDone?: () => void;
  id: string | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [{ data }] = useQuery({
    query: MemberDocument,
    variables: { id: id ?? '' },
    pause: !id,
  });
  const member = data?.member ?? null;
  if (id && !member) {
    return (
      <DeskPanel title="العضو" onClose={onClose} inline={inline}>
        {data ? (
          <Alert type="warning" showIcon message="العضو غير موجود" />
        ) : (
          <Skeleton active paragraph={{ rows: 10 }} />
        )}
      </DeskPanel>
    );
  }
  return (
    <MemberEditor
      member={member}
      inline={inline}
      formOnly={formOnly}
      onDone={onDone}
      onClose={onClose}
      onOpen={onOpen}
      onDirtyChange={onDirtyChange}
    />
  );
}

function MemberEditor({
  member,
  inline,
  formOnly,
  onDone,
  onClose,
  onOpen,
  onDirtyChange,
}: {
  inline: boolean;
  formOnly: boolean;
  onDone?: () => void;
  member: MemberDetails | null;
  onClose: () => void;
  onOpen: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const canEdit = useIsCommittee();
  const { modal, message } = App.useApp();
  const [form] = Form.useForm<MemberFormValues>();
  const [saved, setSaved] = useState(member ? valuesOf(member) : EMPTY);
  const watched = Form.useWatch([], form) as MemberFormValues | undefined;
  useDirty({ ...saved, ...watched }, saved, onDirtyChange);
  const contactPhone = Form.useWatch('contactPhone', form);
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching: creating }, create] = useMutation(CreateMemberDocument);
  const [{ fetching: updating }, update] = useMutation(UpdateMemberDocument);
  const [leaving, setLeaving] = useState(false);

  async function save(values: MemberFormValues) {
    setProblem(null);
    const fields = toMemberFields({ ...EMPTY, ...values });
    if (!member) {
      const r = await create({ member: fields }, peopleContext);
      const createdId = r.data?.createMember?.member?.id;
      if (r.error || !createdId) {
        setProblem(describeError(r.error, 'تعذّر إضافة العضو.', MEMBER_ERRORS));
        return;
      }
      message.success(`أُضيف العضو ${fields.firstName} ${fields.lastName}`);
      onDirtyChange(false);
      onOpen(createdId);
      return;
    }
    const before = member as Record<string, unknown>;
    const changes = Object.entries(fields).filter(
      ([key, value]) => value !== (before[key] ?? null),
    );
    if (!changes.length) {
      message.info('لا تغيير');
      onDone?.();
      return;
    }
    const ok = await modal.confirm({
      title: `حفظ تعديلات ${member.fullName}`,
      width: 560,
      content: (
        <table className="desk-changes">
          <thead>
            <tr>
              <th>الحقل</th>
              <th>قبل</th>
              <th>بعد</th>
            </tr>
          </thead>
          <tbody>
            {changes.map(([key, value]) => (
              <tr key={key}>
                <td>{FIELD_LABELS[key as keyof typeof FIELD_LABELS]}</td>
                <td className="is-before">{show(key, before[key])}</td>
                <td className="is-after">{show(key, value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
      okText: 'حفظ',
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await update(
      { id: member.id, patch: Object.fromEntries(changes) },
      peopleContext,
    );
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر حفظ التعديلات.', MEMBER_ERRORS));
      return;
    }
    setSaved({ ...saved, ...values });
    message.success('حُفظت التعديلات');
    onDirtyChange(false);
    onDone?.();
  }

  const formView = (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      disabled={!canEdit || member?.status === 'LEFT'}
      initialValues={saved}
      onFinish={save}
      className="desk-member-form"
    >
      <div className="desk-form-grid">
        <Form.Item
          name="firstName"
          label="الاسم الشخصي"
          rules={[{ required: true, whitespace: true, message: 'مطلوب' }]}
        >
          <Input autoFocus={!member} autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="lastName"
          label="الاسم العائلي"
          rules={[{ required: true, whitespace: true, message: 'مطلوب' }]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="fatherName" label="اسم الأب (اختياري)">
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="sex" label="الجنس">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: 'MALE', label: 'ذكر' },
              { value: 'FEMALE', label: 'أنثى' },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="memberAddress"
          label="العنوان"
          extra="فارغ: عنوان الدوار من الإعدادات."
          className="is-wide"
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="phone"
          label="هاتف العضو"
          rules={[phoneRule]}
          extra={PHONE_HINT}
        >
          <Input type="tel" className="ltr-num" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="contactPhone"
          label="هاتف قريب (اختياري)"
          rules={[phoneRule]}
          extra="إن وُجد، تُرسل إليه الرسائل بدل رقم العضو."
        >
          <Input type="tel" className="ltr-num" autoComplete="off" />
        </Form.Item>
        {contactPhone?.trim() && (
          <Form.Item
            name="contactName"
            label="صاحب رقم القريب، مثلاً «ابنه عبد الله»"
            className="is-wide"
          >
            <Input autoComplete="off" />
          </Form.Item>
        )}
        <Form.Item name="notifyChannel" label="طريقة التواصل">
          <Select
            options={(Object.keys(CHANNEL_LABELS) as NotifyChannel[]).map(
              (c) => ({ value: c, label: CHANNEL_LABELS[c] }),
            )}
          />
        </Form.Item>
        <Form.Item
          name="email"
          label="البريد الإلكتروني (اختياري)"
          rules={[{ type: 'email', message: 'بريد إلكتروني غير صحيح' }]}
        >
          <Input className="ltr-num" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="committeeRole"
          label="العضوية في اللجنة"
          className="is-wide"
          extra="من له بريد إلكتروني يتوصل بالتقرير الأسبوعي."
        >
          <Select
            allowClear
            placeholder="ليس عضواً في اللجنة"
            options={COMMITTEE_ROLES.map((role) => ({
              value: role,
              label: COMMITTEE_ROLE_LABELS[role],
            }))}
          />
        </Form.Item>
      </div>
      {canEdit && member?.status !== 'LEFT' && (
        <div className="desk-actions">
          <Button
            type="primary"
            htmlType="submit"
            loading={creating || updating}
            title="Ctrl+S"
          >
            {member ? 'حفظ' : 'إضافة العضو'}
          </Button>
          {onDone && (
            <Button
              onClick={() => {
                onDirtyChange(false);
                onDone();
              }}
            >
              إلغاء
            </Button>
          )}
        </div>
      )}
    </Form>
  );

  if (formOnly) {
    return (
      <div className="ui-form">
        {problem && <Alert type="error" showIcon message={problem} />}
        {formView}
      </div>
    );
  }

  return (
    <DeskPanel
      title={member ? (member.fullName ?? 'العضو') : 'عضو جديد'}
      recordId={member?.id}
      onClose={onClose}
      onSave={() => form.submit()}
      inline={inline}
    >
      {member && !inline && <MemberSummary member={member} />}
      {problem && <Alert type="error" showIcon message={problem} />}

      {formView}

      {member && (
        <>
          <ShartEditor member={member} canEdit={canEdit} />
          <section className="desk-panel-section">
            <h3>الوثائق</h3>
            <div className="desk-row-actions">
              <a href={member.statementLink} target="_blank" rel="noreferrer">
                {DOCUMENT_TITLES.statement}
              </a>
              {member.unpaid.totalCount > 0 && (
                <a href={member.noticeLink} target="_blank" rel="noreferrer">
                  {DOCUMENT_TITLES.notice}
                </a>
              )}
            </div>
          </section>
          <section className="desk-panel-section">
            <h3>العدادات ({present(member.meters.nodes).length})</h3>
            {present(member.meters.nodes).map((m) => (
              <Link key={m.id} to={`/meters/${m.id}`} className="desk-line">
                <span>{m.label}</span>
                <Tag
                  color={m.meterStatus === 'CONNECTED' ? 'green' : 'default'}
                >
                  {METER_STATUS_LABELS[m.meterStatus]}
                </Tag>
              </Link>
            ))}
            {canEdit && member.status === 'ACTIVE' && (
              <Link to={`/meters?panel=new&member=${member.id}`}>
                <Button size="small" icon={<PlusOutlined />}>
                  إضافة عداد
                </Button>
              </Link>
            )}
          </section>
          <section className="desk-panel-section">
            <h3>الانخراط</h3>
            <div>
              <Tag color={member.status === 'ACTIVE' ? 'green' : 'default'}>
                {MEMBER_STATUS_LABELS[member.status]}
              </Tag>
              {member.status === 'LEFT' && member.leftAt && (
                <span className="field-hint">
                  منذ {fullDate(member.leftAt)}
                  {member.leftReason ? ` · ${member.leftReason}` : ''}
                </span>
              )}
              <span className="field-hint">
                {' '}
                · منخرط منذ {fullDate(member.joinedAt)}
              </span>
            </div>
            {present(member.memberStatusChanges.nodes).map((c) => (
              <div key={c.id} className="field-hint">
                {fullDate(c.changedOn)}: {MEMBER_STATUS_LABELS[c.toStatus]} ·{' '}
                {c.reason}
                {c.changedBy ? ` · ${c.changedBy}` : ''}
              </div>
            ))}
            {canEdit && (
              <Button
                size="small"
                danger={member.status === 'ACTIVE'}
                onClick={() => setLeaving(true)}
              >
                {member.status === 'ACTIVE'
                  ? 'مغادرة الجمعية'
                  : 'إعادة الانخراط'}
              </Button>
            )}
          </section>
        </>
      )}
      {leaving && member && (
        <MemberStatusDialog member={member} onClose={() => setLeaving(false)} />
      )}
    </DeskPanel>
  );
}

export function MemberSummary({
  member,
  links = true,
}: {
  member: MemberDetails;
  /** To the member's bills and payments (the page has them as tabs). */
  links?: boolean;
}) {
  return (
    <div className="desk-panel-tags">
      {member.committeeRole && (
        <Tag color="purple">{COMMITTEE_ROLE_LABELS[member.committeeRole]}</Tag>
      )}
      {member.notifyChannel === 'NONE' ? (
        <Tag>{CHANNEL_LABELS.NONE}</Tag>
      ) : (
        <Tag color={isReachable(member) ? 'green' : 'orange'}>
          {CHANNEL_LABELS[member.notifyChannel]} ·{' '}
          {PHONE_STATUS_LABELS[member.phoneStatus ?? 'MISSING']}
        </Tag>
      )}
      {member.unpaid.totalCount > 0 && (
        <Tag color="orange">
          {billsCount(member.unpaid.totalCount)} غير مؤداة
        </Tag>
      )}
      {member.status === 'LEFT' && <Tag>غادر الجمعية</Tag>}
      {links && (
        <>
          <Link to={`/bills?member=${member.id}`}>فواتير العضو</Link>
          <Link to={`/payments?member=${member.id}`}>أداءاته</Link>
        </>
      )}
    </div>
  );
}

/**
 * The people a member pays shart for (arqam doc 45). Each change is saved
 * at once (a removal after a confirmation) and applies from the next bill.
 */
export function ShartEditor({
  member,
  canEdit,
  bare = false,
}: {
  member: MemberDetails;
  canEdit: boolean;
  /** Inside a page's section, which has the title (doc 60). */
  bare?: boolean;
}) {
  const { modal, message } = App.useApp();
  const people = present(member.shartPeople.nodes);
  const [, create] = useMutation(CreateShartPersonDocument);
  const [, rename] = useMutation(RenameShartPersonDocument);
  const [, remove] = useMutation(DeleteShartPersonDocument);
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [problem, setProblem] = useState<string | null>(null);

  async function add() {
    if (!name.trim()) return setProblem('الاسم مطلوب');
    const r = await create(
      { memberId: member.id, name: name.trim() },
      peopleContext,
    );
    if (r.error) {
      return setProblem(describeError(r.error, 'تعذّرت الإضافة.', SHART_ERRORS));
    }
    setName('');
    setProblem(null);
    message.success('أُضيف إلى الشرط');
  }

  async function saveRename() {
    if (!editing) return;
    if (!editing.name.trim()) return setProblem('الاسم مطلوب');
    const r = await rename(
      { id: editing.id, name: editing.name.trim() },
      peopleContext,
    );
    if (r.error) {
      return setProblem(
        describeError(r.error, 'تعذّر تغيير الاسم.', SHART_ERRORS),
      );
    }
    setEditing(null);
    setProblem(null);
  }

  async function confirmRemove(person: { id: string; name: string }) {
    const ok = await modal.confirm({
      title: 'إزالة من الشرط',
      content: `إزالة «${person.name}»؟ لن يُحتسب في الفواتير المقبلة.`,
      okText: 'أزله',
      okButtonProps: { danger: true },
      cancelText: 'رجوع',
    });
    if (!ok) return;
    const r = await remove({ id: person.id }, peopleContext);
    if (r.error) {
      message.error(describeError(r.error, 'تعذّرت الإزالة.', SHART_ERRORS));
    }
  }

  return (
    <section className={bare ? 'ui-stack' : 'desk-panel-section'}>
      {!bare && (
        <>
          <h3>
            الشرط (خدمات الإمام):{' '}
            {people.length ? peopleCount(people.length) : 'لا أحد'}
          </h3>
          <div className="field-hint">
            فرد متزوج من الأسرة لكل سطر. تُطبَّق التغييرات من الفاتورة المقبلة.
          </div>
        </>
      )}
      {bare && people.length === 0 && <div className="ui-empty">لا أحد</div>}
      {people.map((p) =>
        editing?.id === p.id ? (
          <div key={p.id} className="desk-shart-row">
            <Input
              size="small"
              autoFocus
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              onPressEnter={saveRename}
            />
            <Button size="small" type="primary" onClick={saveRename}>
              حفظ
            </Button>
            <Button size="small" onClick={() => setEditing(null)}>
              إلغاء
            </Button>
          </div>
        ) : (
          <div key={p.id} className="desk-shart-row">
            <span className={isUnnamed(p.name) ? 'is-missing' : undefined}>
              {p.name}
            </span>
            {canEdit && (
              <>
                <Button
                  size="small"
                  type={isUnnamed(p.name) ? 'primary' : 'text'}
                  icon={<EditOutlined />}
                  onClick={() =>
                    setEditing({
                      id: p.id,
                      name: isUnnamed(p.name) ? '' : p.name,
                    })
                  }
                >
                  {isUnnamed(p.name) ? 'تسمية' : ''}
                </Button>
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`إزالة ${p.name}`}
                  onClick={() => confirmRemove(p)}
                />
              </>
            )}
          </div>
        ),
      )}
      {canEdit && (
        <div className="desk-shart-row">
          <Input
            size="small"
            placeholder="اسم الفرد"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onPressEnter={add}
          />
          <Button size="small" icon={<PlusOutlined />} onClick={add}>
            إضافة
          </Button>
        </div>
      )}
      {problem && <div className="field-error">{problem}</div>}
    </section>
  );
}

/** Leaving (or rejoining) the association, with a reason (arqam doc 41). */
export function MemberStatusDialog({
  member,
  onClose,
}: {
  member: MemberDetails;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const leaving = member.status === 'ACTIVE';
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(todayInMorocco());
  const [problem, setProblem] = useState<string | null>(null);
  const [{ fetching }, change] = useMutation(ChangeMemberStatusDocument);
  const active = present(member.meters.nodes).filter((m) =>
    ['CONNECTED', 'SUSPENDED', 'DAMAGED'].includes(m.meterStatus),
  );

  async function save() {
    if (!reason.trim()) return setProblem('اكتب السبب.');
    const r = await change(
      {
        input: {
          memberId: member.id,
          status: leaving ? 'LEFT' : 'ACTIVE',
          reason: reason.trim(),
          changedOn: date || null,
        },
      },
      peopleContext,
    );
    if (r.error) {
      setProblem(describeError(r.error, 'تعذّر الحفظ.', MEMBER_ERRORS));
      return;
    }
    message.success(leaving ? 'سُجّلت مغادرة العضو' : 'أُعيد انخراط العضو');
    onClose();
  }

  return (
    <Modal
      open
      title={
        leaving
          ? `مغادرة ${member.fullName}`
          : `إعادة انخراط ${member.fullName}`
      }
      okText={leaving ? 'سجّل المغادرة' : 'أعد الانخراط'}
      okButtonProps={{
        danger: leaving,
        disabled: leaving && active.length > 0,
      }}
      cancelText="إلغاء"
      confirmLoading={fetching}
      onOk={save}
      onCancel={onClose}
    >
      <div className="desk-dialog">
        {leaving && active.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            message="للعضو عدادات غير مغلقة"
            description={`أغلق أو استرجع أولاً: ${active.map((m) => m.label).join('، ')}.`}
          />
        ) : (
          leaving && (
            <div className="field-hint">
              تبقى فواتيره غير المؤداة ظاهرة، ولا يُضاف له عداد.
            </div>
          )
        )}
        <Input.TextArea
          autoSize={{ minRows: 2 }}
          placeholder="السبب (مطلوب)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <Input
          type="date"
          className="ltr-num"
          max={todayInMorocco()}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}
